import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { Star, ShoppingCart, Loader2, Truck, Shield, RotateCcw, CheckCircle2, Car, Minus, Plus, AlertTriangle, Hash, Boxes, Repeat } from 'lucide-react';
import ProductGallery from '../components/ProductGallery';
import Breadcrumb from '../components/Breadcrumb';
import { AlsoInBlock } from '../components/AlsoInBlock';
import { RelatedLinksBlock } from '../components/RelatedLinksBlock';
import { RelatedGuidesBlock } from '../components/RelatedGuidesBlock';
import SimilarProducts from '../components/SimilarProducts';
import CrossSellSection from '../components/CrossSellSection';
import EquivalentProducts from '../components/EquivalentProducts';
import ProductFitments from '../components/ProductFitments';
import RecentlyViewed from '../components/RecentlyViewed';
import WishlistButton from '../components/WishlistButton';
import { pushRecent } from '../services/recentStore';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReviewsSection from '../components/ReviewsSection';
import CustomerPhotosStrip from '../components/CustomerPhotosStrip';
import TopProductBadge from '../components/TopProductBadge';
import ProductQuestions from '../components/ProductQuestions';
import ProductQuickFaq from '../components/ProductQuickFaq';
import { subcategoryPath, parentOf } from '../services/catalogPath';
import { catName, subName, prodName, prodDesc, prodSeo, useTerms } from '../services/catalogI18n';
import FaqSection from '../components/FaqSection';
import { renderMarkdown } from '../services/markdown';
import { Button } from '../components/ui/button';
import { productsAPI, cartAPI, categoriesAPI, crossSellAPI } from '../services/api';
import { ga } from '../services/ga';
import { specTemplate, buildSpecRows } from '../services/specs';
import TyreProductBadges, { tyreSpecRows, wheelSpecRows } from '../components/tyres/TyreProductBadges';
import PhotoDiagnosticModal from '../components/PhotoDiagnosticModal';
import { Camera } from 'lucide-react';

const TECH_CATEGORIES = new Set(['freinage', 'motorisation', 'embrayage', 'courroies-chaines-galets', 'suspension', 'amortissement', 'direction', 'echappement', 'allumage-et-prechauffage', 'refroidissement-moteur', 'systeme-d-alimentation', 'boite-de-vitesse', 'arbres-de-transmission-et-differentiels', 'roulements', 'composants-pour-la-suralimentation', 'filtration']);
import { useSelectedVehicle, vehicleStore } from '../services/vehicleStore';
import { usePageMeta } from '../services/seo';
import { useProductJsonLd, seoImageUrl, enrichDescription } from '../services/productJsonLd';
import { availabilityOf, packOptions, tierFor } from '../services/stock';
import PackSelector from '../components/PackSelector';
import PriceTiers from '../components/PriceTiers';
import TyreBundleShortcut from '../components/tyres/TyreBundleShortcut';
import ProPriceHint from '../components/ProPriceHint';
import BestPriceBadge from '../components/BestPriceBadge';
import MobileBuyBar from '../components/MobileBuyBar';
import { useT } from '../services/i18n';
import ShareButtons from '../components/ShareButtons';
import NotifyMeButton from '../components/NotifyMeButton';
import VehicleFitBadge from '../components/VehicleFitBadge';
import CompareButton from '../components/CompareButton';
import { useShippingSummary, fmtEuro } from '../services/shippingInfo';
import { ListChecks, ShieldCheck } from 'lucide-react';
import EquivalencesTable from '../components/EquivalencesTable';
import FuelBadge from '../components/FuelBadge';
import PackEssenceCard from '../components/PackEssenceCard';
import { vehicleEnergy } from '../services/fuel';

const FUEL_LABELS = { essence: 'Essence', diesel: 'Diesel', hybride: 'Hybride', 'essence/électrique': 'Essence/électrique', 'diesel/électrique': 'Diesel/électrique', 'essence/gpl': 'Essence / gaz combustible liquéfié (GPL)', 'essence/éthanol': 'Essence/éthanol', 'essence/éthanol/gaz': 'Essence / éthanol / gaz', 'électrique': 'Électrique', gpl: 'GPL' };

const ruleMatches = (rule, vehicle) =>
  rule.brand.toUpperCase() === vehicle.brand.toUpperCase() &&
  (!rule.models?.length || rule.models.includes(vehicle.model)) &&
  (!rule.fuels?.length || rule.fuels.includes(vehicle.engine?.fuel)) &&
  (!rule.engine_names?.length || rule.engine_names.includes(vehicle.engine?.name));

const ProductPage = () => {
  const { t, lang } = useT();
  const tt = useTerms(lang);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const vehicle = useSelectedVehicle();
  const shipInfo = useShippingSummary();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [packId, setPackId] = useState(null);
  const step = product && !packId ? (product.sale_multiple || 1) : 1;
  const [adding, setAdding] = useState(false);
  const [showDiag, setShowDiag] = useState(false);
  const [added, setAdded] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    categoriesAPI.getAll().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setProduct(null);
    setNotFound(false); setLoadError(false);
    setActiveImage(0);
    setPackId(null);
    setQuantity(1);
    window.scrollTo(0, 0);
    productsAPI.getById(id).then(p => { if (cancelled) return; setProduct(p); setQuantity(p.sale_multiple || 1); pushRecent(p.id); ga.viewItem(p); }).catch((e) => { if (cancelled) return; const redirect = e.response?.status === 404 && e.response?.data?.detail?.redirect; if (redirect) { toast.info(t('product.movedToCategory')); navigate(redirect, { replace: true }); } else if (e.response?.status === 404) setNotFound(true); else setLoadError(true); });
    return () => { cancelled = true; };
  }, [id]);

  const [equivalents, setEquivalents] = useState(null);
  // Arrivée depuis une recommandation e-mail (?xs=<règle>&xsp=email) : clic puis ajout tracés pour l'attribution du CA
  const xsRef = useMemo(() => { const q = new URLSearchParams(location.search); return q.get('xsp') === 'email' ? { rule_id: q.get('xs') || null, placement: 'email' } : null; }, [location.search]);
  useEffect(() => {
    if (product?.id && xsRef) crossSellAPI.event({ type: 'click', placement: 'email', product_id: product.id, rule_id: xsRef.rule_id });
  }, [product?.id, xsRef]);
  useEffect(() => {
    if (!product?.id) { setEquivalents(null); return undefined; }
    let cancelled = false;
    productsAPI.getEquivalents(product.id).then(d => { if (!cancelled) setEquivalents(d); }).catch(() => { if (!cancelled) setEquivalents({ items: [] }); });
    return () => { cancelled = true; };
  }, [product?.id]);
  const [similar, setSimilar] = useState([]);
  useEffect(() => {
    if (!product?.id) { setSimilar([]); return undefined; }
    let cancelled = false;
    productsAPI.getSimilar(product.id, 8, vehicleStore.toParams(vehicle)).then(list => { if (!cancelled) setSimilar(list); }).catch(() => { if (!cancelled) setSimilar([]); });
    return () => { cancelled = true; };
  }, [product?.id, vehicle]);
  // cheapest alternative among admin-linked equivalents and vehicle-compatible similar products
  const cheaperAlt = product && [...(equivalents?.items || []).map(p => ({ ...p, _source: 'equivalent-products' })), ...similar.map(p => ({ ...p, _source: 'similar-products' }))]
    .filter(p => p.price < product.price).sort((a, b) => a.price - b.price)[0];

  const metaCategory = product ? (categories.find(c => c.slug === product.category)?.name || product.category) : '';
  const pseo = prodSeo(product, lang);
  usePageMeta(product ? {
    title: pseo.seo_title || `${prodName(product, lang)} ${product.brand} — ${t('product.refLabel')} ${product.ref}`,
    description: enrichDescription(pseo.meta_description || `${prodName(product, lang)} ${product.brand} (réf. ${product.ref}${product.type ? `, ${product.type}` : ''}) à ${product.price.toFixed(2)} € TTC. ${metaCategory} pour ${product.compatibility?.length ? [...new Set(product.compatibility.map(r => r.brand))].slice(0, 4).join(', ') : 'tous véhicules'}. Livraison 2-5 jours, garantie 2 ans, retour 14 jours.`, product),
    keywords: pseo.secondary_keywords || [],
    image: `${process.env.REACT_APP_BACKEND_URL}/api/share/produit/${product.slug || product.id}.png${lang !== 'fr' ? `?lang=${lang}` : ''}`,
    path: `/produit/${product.slug || product.id}`,
    type: 'product',
  } : notFound ? { title: t('product.notFound'), noindex: true } : {});
  useProductJsonLd(product, { categoryName: metaCategory, canonicalPath: product ? `/produit/${product.slug || product.id}` : '' });

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await cartAPI.addItem(product.id, quantity, packId);
      if (xsRef) crossSellAPI.event({ type: 'add', placement: 'email', product_id: product.id, rule_id: xsRef.rule_id });
      setAdded(true);
      setTimeout(() => setAdded(false), 2500);
    } catch (e) {
      // toast already shown by cartAPI
    } finally {
      setAdding(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-100"><Header /><main className="max-w-3xl mx-auto px-4 py-16 text-center text-gray-600" data-testid="product-load-error"><p>{t('common.loadError')}</p><button type="button" onClick={() => window.location.reload()} className="mt-4 text-orange-600 underline" data-testid="product-retry">{t('common.retry')}</button></main><Footer /></div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="product-not-found">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('product.notFound')}</h1>
          <Link to="/" className="text-blue-600 hover:underline">{t('product.backHome')}</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product || (id !== product.id && id !== product.slug)) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        <Footer />
      </div>
    );
  }

  if (product.slug && id === product.id) {
    return <Navigate to={`/produit/${product.slug}${location.search}${location.hash}`} replace />;
  }

  const images = product.images?.length ? product.images : [product.image];
  const universal = !product.compatibility?.length;
  const vehicleEnergyKey = vehicleEnergy(vehicle?.engine?.fuel);
  const fuelKo = !!(vehicle && product.fuel_types?.length && vehicleEnergyKey && !product.fuel_types.includes(vehicleEnergyKey));
  const compatibleWithVehicle = vehicle ? (!fuelKo && (universal || product.compatibility.some(r => ruleMatches(r, vehicle)))) : null;
  const universalBadge = universal && !(product.fuel_types?.length && vehicleEnergyKey);
  const rulesByBrand = (product.compatibility || []).reduce((acc, r) => {
    acc[r.brand] = acc[r.brand] || [];
    acc[r.brand].push(r);
    return acc;
  }, {});

  const categoryObj = categories.find(c => c.slug === product.category);
  const categoryName = catName(categoryObj, lang) || product.category;
  const subcategoryName = product.subcategory ? subName(categoryObj, product.subcategory, lang) : '';
  const avail = availabilityOf(product);
  const selectedPack = packOptions(product).find(o => (o.id || null) === (packId || null)) || packOptions(product)[0];
  const maxQty = Math.max(step, Math.min(selectedPack.units === 1 ? 99 : 20, Math.floor(avail.total / selectedPack.units)));
  const soldBy = step > 1;
  const activeTier = selectedPack.units === 1 ? tierFor(product, quantity) : null;
  const unitPrice = activeTier ? Math.round(product.price * (1 - activeTier.discount_pct / 100) * 100) / 100 : selectedPack.price;
  const extraRefs = product.refs || [];
  const oemRefs = product.oem_refs || [];
  const refBrands = product.ref_brands || {};
  const refLabel = (r) => (refBrands[r] ? `${refBrands[r]} ${r}` : r);
  const template = specTemplate(categoryObj, product.subcategory);
  const attrRows = Object.entries(product.attributes || {}).filter(([, v]) => v);
  const equivTotal = extraRefs.length + oemRefs.length;
  const equivRow = equivTotal ? [[t('spec.equivalences'), equivTotal, 'equivlink']] : [];
  const specs = template ? [
    ...buildSpecRows(product, template).map(([k, v, kind]) => [k, v, kind]),
    ...equivRow,
    [t('spec.availability'), avail.label],
  ] : [
    [t('spec.manufacturer'), product.brand, 'brand'],
    [t('spec.reference'), product.ref],
    ...equivRow,
    [t('spec.category'), categoryName + (product.subcategory ? ` › ${subcategoryName}` : '')],
    ...(product.tyre ? tyreSpecRows(product.tyre) : product.wheel ? wheelSpecRows(product.wheel) : [[t('spec.features'), product.type || '—']]),
    ...attrRows,
    ...(product.ean ? [[t('spec.ean'), product.ean]] : []),
    [t('spec.condition'), product.condition || t('spec.new')],
    ...((product.sale_multiple || 1) > 1 ? [[t('spec.soldBy'), `${product.sale_multiple}${product.sale_multiple_label ? ` (${product.sale_multiple_label})` : ''}`]] : []),
    ...(product.packs?.length ? [[t('spec.packagings'), [t('common.unit'), ...product.packs.map(p => `${p.label} (${p.units} u.)`)].join(', ')]] : []),
    [t('spec.avgRating'), t('spec.avgRatingValue', { r: product.rating.toFixed(1), n: product.reviews })],
    [t('spec.warranty'), t('spec.warrantyValue')],
    [t('spec.availability'), avail.label],
  ];

  const from = location.state?.from;
  const fromMatches = from && (
    (from.category === product.category && (!from.subcategory || from.subcategory === product.subcategory || parentOf(categories.find(c => c.slug === from.category), product.subcategory) === from.subcategory))
    || (product.extra_categories || []).some(x => x.category === from.category && (!from.subcategory || x.subcategory === from.subcategory))
  );
  const crumb = fromMatches ? { category: from.category, subcategory: from.subcategory || null } : { category: product.category, subcategory: product.subcategory || null };
  const crumbCategoryObj = categories.find(c => c.slug === crumb.category);
  const crumbCategoryName = catName(crumbCategoryObj, lang) || crumb.category;
  const crumbSubName = crumb.subcategory ? subName(crumbCategoryObj, crumb.subcategory, lang) : '';
  const crumbParent = parentOf(crumbCategoryObj, crumb.subcategory);

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8" data-testid="product-page">
        <Breadcrumb
          testId="product-breadcrumb"
          items={[
            { label: crumbCategoryName, to: `/pieces/${crumb.category}` },
            crumbParent ? { label: subName(crumbCategoryObj, crumbParent, lang), to: subcategoryPath(crumbCategoryObj, crumbParent) } : null,
            crumb.subcategory ? { label: crumbSubName, to: subcategoryPath(crumbCategoryObj || { slug: crumb.category }, crumb.subcategory) } : null,
            { label: product.brand, to: `/marque/${encodeURIComponent(product.brand)}` },
            { label: prodName(product, lang) },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
          {/* Gallery */}
          <ProductGallery images={images} name={prodName(product, lang)} discount={product.discount} activeImage={activeImage} onChange={setActiveImage} />

          {/* Info */}
          <div className="lg:col-span-3 bg-white rounded-xl shadow-sm p-6">
            <VehicleFitBadge garage vehicle={vehicle} fits={compatibleWithVehicle} universal={universalBadge} fuelKo={fuelKo} fuelTypes={product.fuel_types} partsLink={product.subcategory ? subcategoryPath(categoryObj || { slug: product.category }, product.subcategory) : `/pieces/${product.category}`} />
            {product.fuel_types?.length > 0 && <div className="mb-2 flex items-center gap-2 text-xs text-gray-500" data-testid="product-fuel-restriction"><span>{t('fuelR.reserved')}</span><FuelBadge types={product.fuel_types} testId="product-fuel-badge" /></div>}
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              <Link to={`/marque/${encodeURIComponent(product.brand)}`} className="hover:text-orange-600 transition-colors" title={t('product.allParts', { brand: product.brand })} data-testid="product-brand-link">
                <BrandLogo brand={product.brand} size="sm" testId="product-brand-logo" />
              </Link>
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1" data-testid="product-title">{pseo.h1 || prodName(product, lang)}</h1>
            {pseo.h1 && pseo.h1 !== prodName(product, lang) && <p className="text-sm text-gray-500" data-testid="product-name-sub">{prodName(product, lang)}</p>}
            {product.type && <p className="text-gray-600 mt-1" data-testid="product-type">{tt(product.type)}</p>}
            <TyreProductBadges product={product} />
            <div className="mt-2"><ShareButtons kind="product" itemId={product.id} payload={{ product_id: product.id }} gaParams={{ item_name: product.name, item_brand: product.brand }} title={`${pseo.h1 || prodName(product, lang)} ${product.brand} — ${t('product.refLabel')} ${product.ref}`} url={`${window.location.origin}/produit/${product.slug || product.id}${lang !== 'fr' ? `?lang=${lang}` : ''}`} /></div>
            <p className="text-sm text-gray-400 mt-1">{t('product.manufacturerRef')} <span className="font-mono text-gray-600" data-testid="product-ref">{product.ref}</span></p>
            {(extraRefs.length > 0 || oemRefs.length > 0) && (
              <div className="mt-2 flex flex-wrap gap-1.5 items-center" data-testid="product-refs">
                <Hash size={14} className="text-gray-400" />
                {extraRefs.slice(0, 4).map(r => (
                  <span key={r} data-testid={`product-extra-ref-${r}`} className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded" title={t('product.equivalentRef')}>{refLabel(r)}</span>
                ))}
                {extraRefs.length > 4 && (
                  <a href="#equivalences" className="text-xs text-blue-600 hover:underline" data-testid="product-refs-more">{t('equiv.moreAlt', { n: extraRefs.length - 4 })}</a>
                )}
                {oemRefs.slice(0, 1).map(r => (
                  <span key={r} data-testid={`product-oem-ref-${r}`} className="text-xs font-mono bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 rounded" title={t('product.oemRef')}>OEM {refLabel(r)}</span>
                ))}
                {oemRefs.length > 1 && (
                  <a href="#equivalences" className="text-xs text-blue-600 hover:underline" data-testid="product-oem-more">{t('product.moreOem', { n: oemRefs.length - 1, count: oemRefs.length - 1 })}</a>
                )}
              </div>
            )}

            <div className="flex items-center gap-2 mt-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} className={i < Math.round(product.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
              ))}
              <a href="#avis" className="text-sm text-blue-600 hover:underline" data-testid="product-reviews-link">{t('product.reviews', { n: product.reviews, count: product.reviews })}</a>
              <TopProductBadge product={product} size="lg" testId="product-top-badge" />
            </div>

            <div className="flex items-end gap-3 mt-6 flex-wrap">
              {selectedPack.units === 1 && (activeTier ? <span className="text-lg text-gray-400 line-through" data-testid="product-base-price">{product.price.toFixed(2)} €</span> : product.original_price && <span className="text-lg text-gray-400 line-through">{product.original_price.toFixed(2)} €</span>)}
              <span className="text-4xl font-bold text-gray-900" data-testid="product-price">{unitPrice.toFixed(2)} €</span>
              <ProPriceHint price={unitPrice} className="pb-2" />
              <span className="text-xs text-gray-500 pb-2">{t('product.ttcExclShipping')}{selectedPack.units > 1 ? ` · ${selectedPack.label} (${(selectedPack.price / selectedPack.units).toFixed(2)} € ${t('common.perUnit')})` : ''}</span>
              {activeTier && <span className="text-xs font-bold text-white bg-green-600 px-2 py-0.5 rounded-full mb-2" data-testid="product-tier-badge">{t('product.tierBadge', { pct: activeTier.discount_pct, n: activeTier.min_qty })}</span>}
              <span className="pb-1.5"><BestPriceBadge product={product} size="lg" /></span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="product-price-actions">
              {avail.status !== 'out' && <NotifyMeButton product={product} kind="price" />}
              <a href="#caracteristiques" data-testid="product-specs-anchor" onClick={(e) => { e.preventDefault(); document.getElementById('caracteristiques')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1e3a5f] hover:text-orange-600 hover:underline transition-colors">
                <ListChecks size={15} /> {t('product.viewSpecs')}
              </a>
            </div>
            {cheaperAlt && (
              <button type="button" data-testid="cheaper-alternative-hint" onClick={() => document.getElementById(cheaperAlt._source)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors">
                <Repeat size={14} /> {t(cheaperAlt._source === 'similar-products' ? 'product.cheaperAltCompat' : 'product.cheaperAlt', { price: cheaperAlt.price.toFixed(2), brand: cheaperAlt.brand })} <span className="text-blue-500">(−{(product.price - cheaperAlt.price).toFixed(2)} €)</span>
              </button>
            )}
            {soldBy && (
              <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#1e3a5f] bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full" data-testid="product-sale-multiple">
                <Boxes size={14} /> {t('product.soldByDetail', { n: product.sale_multiple, label: product.sale_multiple_label ? ` (${product.sale_multiple_label})` : '', total: (unitPrice * product.sale_multiple).toFixed(2), unitLabel: product.sale_multiple_label || t('product.theN', { n: product.sale_multiple }), unit: unitPrice.toFixed(2) })}
              </p>
            )}
            {activeTier && <p className="text-xs text-green-700 mt-1" data-testid="product-tier-total">{t('product.totalFor', { n: quantity })} <strong>{(unitPrice * quantity).toFixed(2)} €</strong> {t('product.insteadOf', { price: (product.price * quantity).toFixed(2) })}</p>}
            <div className="mt-3" data-testid="product-availability">
            {avail.status === 'out' ? (
              <>
                <p className="inline-flex items-center gap-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 px-3 py-1.5 rounded-full" data-testid="product-out-of-stock"><AlertTriangle size={14} /> {t('product.outOfStockHint')}</p>
                <NotifyMeButton product={product} />
              </>
            ) : avail.status === 'supplier' ? (
              <p className="inline-flex items-center gap-2 text-sm font-medium text-blue-800 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full" data-testid="product-supplier-stock"><Truck size={14} /> {avail.label}</p>
            ) : avail.status === 'low' ? (
              <p className="text-sm font-medium text-orange-700" data-testid="product-low-stock">{avail.label}</p>
            ) : (
              <p className="inline-flex items-center gap-2 text-sm font-medium text-green-700" data-testid="product-in-stock"><CheckCircle2 size={14} /> {avail.label}</p>
            )}
            </div>

            <PackSelector product={product} selected={packId} available={avail.total} onSelect={(id) => { setPackId(id); setQuantity(id ? 1 : (product.sale_multiple || 1)); }} />
            {selectedPack.units === 1 && product.tyre ? <TyreBundleShortcut product={product} quantity={quantity} maxQty={maxQty} onSelect={setQuantity} /> : selectedPack.units === 1 && <PriceTiers product={product} quantity={quantity} />}

            <div className="flex flex-wrap items-center gap-4 mt-6" id="product-buy-actions">
              <div className="flex items-center border rounded-lg">
                <button data-testid="product-qty-minus" onClick={() => setQuantity(q => Math.max(step, q - step))} className="p-2.5 hover:bg-gray-50"><Minus size={16} /></button>
                <span className="w-10 text-center font-medium" data-testid="product-qty">{quantity}</span>
                <button data-testid="product-qty-plus" onClick={() => setQuantity(q => Math.min(maxQty, q + step))} className="p-2.5 hover:bg-gray-50"><Plus size={16} /></button>
              </div>
              {TECH_CATEGORIES.has(product.category) && (
                <Button variant="outline" onClick={() => setShowDiag(true)} className="h-11 border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="product-photo-check-button"><Camera size={16} className="mr-2" /> {t('product.photoCheck')}</Button>
              )}
              <Button data-testid="product-add-to-cart" onClick={handleAddToCart} disabled={adding || avail.status === 'out' || selectedPack.units > avail.total} className="bg-orange-500 hover:bg-orange-600 px-8 h-11">
                {adding ? <Loader2 size={18} className="mr-2 animate-spin" /> : added ? <CheckCircle2 size={18} className="mr-2" /> : <ShoppingCart size={18} className="mr-2" />}
                {added ? t('product.added') : selectedPack.units > 1 ? t('product.addPack', { qty: quantity > 1 ? `${quantity} × ` : '', label: selectedPack.label }) : t('common.addToCart')}
              </Button>
                <WishlistButton productId={product.id} showLabel />
              <CompareButton product={product} />
              {added && <Link to="/panier" className="text-sm text-blue-600 hover:underline" data-testid="product-go-cart">{t('product.viewCart')}</Link>}
            </div>
            <MobileBuyBar anchorId="product-buy-actions" price={unitPrice} quantity={quantity} step={step} maxQty={maxQty} onQty={setQuantity} onAdd={handleAddToCart} adding={adding} added={added} disabled={avail.status === 'out' || selectedPack.units > avail.total} />
            <PackEssenceCard product={product} vehicle={vehicle} onAdded={() => cartAPI.get().then(c => setCartIds((c.items || []).map(i => i.product_id))).catch(() => {})} />

            <ProductQuickFaq productId={product.id} />

            <div className="grid grid-cols-3 gap-3 mt-6 text-center">
              {[
                { icon: Truck, label: t('product.delivery'), sub: shipInfo.freeFrom != null ? t('product.deliverySub', { n: fmtEuro(shipInfo.freeFrom).replace(' €', '') }) : t('product.deliverySubFast') },
                { icon: RotateCcw, label: t('product.return'), sub: t('product.returnSub') },
                { icon: Shield, label: t('product.warranty'), sub: t('product.warrantySub') },
              ].map((b, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3">
                  <b.icon size={20} className="mx-auto text-orange-500 mb-1" />
                  <p className="text-xs font-medium text-gray-900">{b.label}</p>
                  <p className="text-[11px] text-gray-500">{b.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-6 -mt-2"><CustomerPhotosStrip productId={product.id} /></div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <section id="caracteristiques" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-24" data-testid="product-specs">
            <h2 className="text-xl font-bold text-gray-900 mb-4">{t('product.specs')}</h2>
            <table className="w-full text-sm">
              <tbody className="divide-y">
                {specs.map(([k, v, kind, total]) => (
                  <tr key={k} data-testid={`product-spec-row-${k}`}>
                    <td className="py-2.5 text-gray-500 w-2/5">{tt(k)}</td>
                    <td className="py-2.5 text-gray-900 font-medium">
                      {kind === 'brand' || k === t('spec.manufacturer') ? <BrandLogo brand={v} size="xs" testId="product-spec-brand-logo" />
                        : kind === 'equivlink' ? <a href="#equivalences" className="text-sm text-blue-600 hover:underline font-medium" data-testid="product-spec-equiv-link">{t('equiv.seeAll', { n: v })}</a>
                        : kind === 'refs' ? <ul className="space-y-0.5">{v.map(item => <li key={item} className="font-mono text-[13px]" data-testid={`product-spec-item-${item}`}>{refBrands[item] && <span className="font-sans text-[11px] font-semibold text-gray-500 uppercase mr-1.5" data-testid={`product-spec-item-brand-${item}`}>{refBrands[item]}</span>}{item}</li>)}{total > v.length && <li><a href="#equivalences" className="text-xs text-blue-600 hover:underline font-sans" data-testid={`product-spec-more-${k}`}>{t('equiv.seeAll', { n: total })}</a></li>}</ul>
                        : kind === 'list' ? <ul className="space-y-0.5">{v.map(item => <li key={item} className="font-mono text-[13px]" data-testid={`product-spec-item-${item}`}>{item}</li>)}</ul>
                        : tt(v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="bg-white rounded-xl shadow-sm p-6" data-testid="product-compatibility">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2 flex-wrap"><Car className="text-orange-500" size={22} /> {t('product.compatibleVehicles')}
              {product.compat_sync?.sources?.length > 0 && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full" data-testid="product-compat-verified" title={product.compat_sync.sources.map(s => `${s.brand || ''} ${s.ref || ''}`.trim()).join(', ')}>
                  <ShieldCheck size={13} /> {t('product.compatVerified', { n: product.compat_sync.sources.length, count: product.compat_sync.sources.length })}
                </span>
              )}
            </h2>
            <ProductFitments productId={product.id} fallback={universal ? (
              <p className="text-sm text-gray-700 flex items-center gap-2" data-testid="product-compat-universal">
                <CheckCircle2 size={16} className="text-green-600" /> {t('product.universal')}
              </p>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                {Object.entries(rulesByBrand).sort().map(([brand, rules]) => (
                  <div key={brand} className="border rounded-lg p-3" data-testid={`product-compat-brand-${brand}`}>
                    <p className="font-semibold text-gray-900 text-sm">{brand}</p>
                    {rules.map((r, idx) => (
                      <div key={idx} className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {r.models?.length
                          ? r.models.map(m => <span key={m} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full">{m}</span>)
                          : <span className="text-xs text-gray-500 italic">{t('product.allModels')}</span>}
                        {r.fuels?.length > 0 && (
                          <span className="text-xs text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full">
                            {r.fuels.map(f => FUEL_LABELS[f] || f).join(' / ')}
                          </span>
                        )}
                        {r.engine_names?.length > 0 && r.engine_names.map(n => (
                          <span key={n} data-testid={`product-compat-engine-${n}`} className="text-xs text-white bg-[#1e3a5f] px-2 py-0.5 rounded-full">{n}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              )} />
          </section>

          <div className="lg:col-span-2"><EquivalencesTable refs={extraRefs} oemRefs={oemRefs} refBrands={refBrands} productId={product.id} /></div>
        </div>

        {prodDesc(product, lang) && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6" data-testid="product-description">
            <h2 className="text-xl font-bold text-gray-900 mb-3">{t('product.description')}</h2>
            <div className="prose-legal text-gray-700 leading-relaxed text-sm sm:text-base [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: renderMarkdown(prodDesc(product, lang)) }} />
          </section>
        )}
        {pseo.faq?.length > 0 && (
          <section className="bg-white rounded-xl shadow-sm p-6 mb-6" data-testid="product-seo-faq-section">
            <FaqSection items={pseo.faq} title={t('product.faqTitle')} testId="product-faq" />
          </section>
        )}

        <AlsoInBlock product={product} categories={categories} current={crumb} />
        <RelatedLinksBlock productId={product.id} />
        <RelatedGuidesBlock productId={product.id} />
        <EquivalentProducts product={product} data={equivalents} />
        <CrossSellSection placement="product" productIds={[product.id]} title={t('xsell.productTitle')} subtitle={t('xsell.productHint')} className="bg-white rounded-xl shadow-sm p-5 mb-6" testId="product-cross-sell" />
        <SimilarProducts productId={product.id} categoryName={categoryName} items={similar} />
        <RecentlyViewed excludeId={product.id} />

        <ReviewsSection productId={product.id} productName={product.name} />

        <ProductQuestions productId={product.id} productName={product.name} />
      </main>
      {showDiag && <PhotoDiagnosticModal product={product} onClose={() => setShowDiag(false)} />}
      <Footer />
    </div>
  );
};

export default ProductPage;
